import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Loader2, X, TrendingUp, Calendar, AlertTriangle, CheckCircle2,
    Pause, Play, ChevronLeft, ChevronRight, Sparkles,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { log } from '@/lib/log';

// Slide indices
const SLIDES = ['kpis', 'milestones', 'risks', 'decisions'];
const SLIDE_DURATION_MS = 30000;
const REFRESH_MS = 60000;

function fmt(v) {
    if (v == null) return '—';
    try { return Number(v).toLocaleString('fr-FR'); } catch { return v; }
}

function daysFromNow(iso) {
    if (!iso) return null;
    const diff = (new Date(iso).getTime() - Date.now()) / 86_400_000;
    return Math.round(diff);
}

/** Smooth animated count-up for numbers — runs whenever `target` changes. */
function useCountUp(target, durationMs = 1100) {
    const [value, setValue] = useState(0);
    const rafRef = useRef(null);
    useEffect(() => {
        const end = Number(target) || 0;
        if (!Number.isFinite(end)) { setValue(end); return; }
        const start = performance.now();
        const from = 0;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / durationMs);
            // easeOutCubic
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(from + (end - from) * eased);
            if (t < 1) rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [target, durationMs]);
    return value;
}

export default function ProjectKiosk() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [slideIndex, setSlideIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [now, setNow] = useState(new Date());
    const [progress, setProgress] = useState(0);

    const load = useCallback(async () => {
        try {
            const [proj, tasks, lines, expenses, discussions] = await Promise.all([
                supabase.from('projects').select('*').eq('id', id).maybeSingle(),
                supabase.from('project_tasks').select('id,title,start_date,end_date,is_milestone,phase')
                    .eq('project_id', id).order('end_date', { ascending: true }).limit(40),
                supabase.from('budget_lines').select('amount_planned,currency').eq('project_id', id),
                supabase.from('expenses').select('amount,currency,status').eq('project_id', id),
                supabase.from('project_discussions').select('*').eq('project_id', id).order('created_at', { ascending: false }).limit(40),
            ]);

            const project = proj?.data || null;
            const milestones = (tasks?.data || [])
                .filter(t => t.is_milestone && t.end_date && new Date(t.end_date) >= new Date(Date.now() - 86_400_000))
                .slice(0, 6);
            const totalBudget = (lines?.data || []).reduce((s, l) => s + Number(l.amount_planned || 0), 0);
            const totalSpent = (expenses?.data || []).reduce((s, e) => s + Number(e.amount || 0), 0);
            const pendingExp = (expenses?.data || []).filter(e => (e.status || '').toLowerCase() === 'pending').length;
            const risks = (discussions?.data || [])
                .filter(d => d.category === 'risk' && !d.is_resolved)
                .slice(0, 3);
            const decisions = (discussions?.data || [])
                .filter(d => d.category === 'decision')
                .slice(0, 5);

            setData({
                project,
                kpis: {
                    budget_planned: totalBudget,
                    budget_spent: totalSpent,
                    consumed_pct: totalBudget ? Math.round((totalSpent / totalBudget) * 100) : 0,
                    tasks_count: (tasks?.data || []).length,
                    milestones_total: (tasks?.data || []).filter(t => t.is_milestone).length,
                    pending_approvals: pendingExp,
                    progress: project?.progress || 0,
                },
                milestones,
                risks,
                decisions,
            });
        } catch (e) {
            log.error('Kiosk load error', e);
        } finally {
            setLoading(false);
        }
    }, [id]);

    // Initial load + refresh
    useEffect(() => {
        load();
        const t = setInterval(load, REFRESH_MS);
        return () => clearInterval(t);
    }, [load]);

    // Clock
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    // Auto-advance slides with progress bar
    useEffect(() => {
        if (paused) return;
        const start = Date.now();
        const t = setInterval(() => {
            const elapsed = Date.now() - start;
            const pct = Math.min(100, (elapsed / SLIDE_DURATION_MS) * 100);
            setProgress(pct);
            if (elapsed >= SLIDE_DURATION_MS) {
                setSlideIndex(i => (i + 1) % SLIDES.length);
                setProgress(0);
            }
        }, 250);
        return () => clearInterval(t);
    }, [slideIndex, paused]);

    // Keyboard nav: arrows / space / Escape
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') navigate(`/app/projects/${id}`);
            else if (e.key === 'ArrowRight') { setSlideIndex(i => (i + 1) % SLIDES.length); setProgress(0); }
            else if (e.key === 'ArrowLeft') { setSlideIndex(i => (i - 1 + SLIDES.length) % SLIDES.length); setProgress(0); }
            else if (e.key === ' ') { e.preventDefault(); setPaused(p => !p); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [id, navigate]);

    const currentSlide = useMemo(() => SLIDES[slideIndex], [slideIndex]);

    if (loading) {
        return (
            <div className="fixed inset-0 bg-ink flex items-center justify-center text-white" data-testid="kiosk-loading">
                <Loader2 className="animate-spin" size={32} />
            </div>
        );
    }
    if (!data?.project) {
        return (
            <div className="fixed inset-0 bg-ink flex flex-col items-center justify-center text-white gap-4">
                <div className="text-2xl font-heading">Projet introuvable</div>
                <Link to="/app/projects" className="text-primary hover:underline">Retour aux projets →</Link>
            </div>
        );
    }

    const { project, kpis, milestones, risks, decisions } = data;
    const currency = project.currency || 'XOF';

    return (
        <div
            className="fixed inset-0 bg-gradient-to-br from-ink via-[#1c1a19] to-[#332c28] text-white overflow-hidden"
            data-testid="project-kiosk"
        >
            {/* Aurora ambient layers (parallax-drift) */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div
                    className="absolute -top-32 -left-32 h-[55vh] w-[55vh] rounded-full kiosk-aurora"
                    style={{ background: 'radial-gradient(closest-side, rgba(212,114,47,0.42), rgba(212,114,47,0) 70%)' }}
                />
                <div
                    className="absolute -bottom-40 -right-32 h-[60vh] w-[60vh] rounded-full kiosk-aurora"
                    style={{ background: 'radial-gradient(closest-side, rgba(57,99,76,0.38), rgba(57,99,76,0) 70%)', animationDelay: '-8s' }}
                />
                <div
                    className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[35vh] w-[35vh] rounded-full kiosk-aurora opacity-50"
                    style={{ background: 'radial-gradient(closest-side, rgba(255,200,150,0.25), rgba(0,0,0,0) 70%)', animationDelay: '-14s' }}
                />
            </div>

            {/* Top bar */}
            <header className="absolute top-0 inset-x-0 px-10 pt-6 flex items-start justify-between z-10 animate-in fade-in duration-700">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-1 flex items-center gap-2">
                        <Sparkles size={12} className="text-primary" />
                        {project.industry || 'Pilotage'} · {project.country || '—'}
                    </div>
                    <h1 className="font-heading text-4xl lg:text-5xl text-white" data-testid="kiosk-project-name">
                        {project.name}
                    </h1>
                </div>
                <div className="flex items-start gap-4">
                    <div className="text-right">
                        <div className="font-mono text-2xl text-white tabular-nums" data-testid="kiosk-clock">
                            {now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="text-[11px] uppercase tracking-wider text-white/40">
                            {now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </div>
                    </div>
                    <button
                        onClick={() => navigate(`/app/projects/${id}`)}
                        aria-label="Quitter"
                        data-testid="kiosk-exit"
                        className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center transition-all hover:scale-105 hover:rotate-90 duration-300"
                    >
                        <X size={16} />
                    </button>
                </div>
            </header>

            {/* Main slide area — animates whenever currentSlide changes */}
            <main
                key={currentSlide}
                className="absolute inset-0 flex items-center justify-center px-10 pt-32 pb-28 kiosk-slide"
                data-testid={`kiosk-slide-${currentSlide}`}
            >
                {currentSlide === 'kpis' && (
                    <SlideKpis kpis={kpis} currency={currency} fmt={fmt} />
                )}
                {currentSlide === 'milestones' && (
                    <SlideMilestones items={milestones} />
                )}
                {currentSlide === 'risks' && (
                    <SlideRisks items={risks} />
                )}
                {currentSlide === 'decisions' && (
                    <SlideDecisions items={decisions} />
                )}
            </main>

            {/* Bottom bar : slide indicator + controls */}
            <footer className="absolute bottom-0 inset-x-0 px-10 pb-6 z-10 animate-in fade-in slide-in-from-bottom-2 duration-700">
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={() => { setSlideIndex(i => (i - 1 + SLIDES.length) % SLIDES.length); setProgress(0); }}
                        className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center transition-all hover:scale-110 hover:-translate-x-0.5 duration-200"
                        data-testid="kiosk-prev"
                    ><ChevronLeft size={14} /></button>
                    <div className="flex-1 flex gap-2" data-testid="kiosk-progress">
                        {SLIDES.map((s, i) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => { setSlideIndex(i); setProgress(0); }}
                                className="flex-1 text-left group"
                            >
                                <div className={`text-[10px] uppercase tracking-wider mb-1.5 transition-colors ${i === slideIndex ? 'text-primary' : 'text-white/40 group-hover:text-white/70'}`}>
                                    {SLIDE_LABEL[s]}
                                </div>
                                <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                                    <div
                                        className={`h-full bg-primary ${i === slideIndex ? 'transition-[width] duration-200 ease-linear shadow-[0_0_10px_rgba(212,114,47,0.7)]' : 'transition-all duration-300'}`}
                                        style={{ width: i < slideIndex ? '100%' : i === slideIndex ? `${progress}%` : '0%' }}
                                    />
                                </div>
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => setPaused(p => !p)}
                        className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center transition-all hover:scale-110 duration-200"
                        data-testid="kiosk-pause"
                    >{paused ? <Play size={13} /> : <Pause size={13} />}</button>
                    <button
                        type="button"
                        onClick={() => { setSlideIndex(i => (i + 1) % SLIDES.length); setProgress(0); }}
                        className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center transition-all hover:scale-110 hover:translate-x-0.5 duration-200"
                        data-testid="kiosk-next"
                    ><ChevronRight size={14} /></button>
                </div>
                <div className="text-center text-[10px] uppercase tracking-wider text-white/30 mt-4">
                    African Valley · Kiosque COPIL · {paused ? 'En pause — barre espace pour reprendre' : 'Rotation auto · 30 s par écran'}
                </div>
            </footer>

            {/* Decorative grain texture */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.04] mix-blend-overlay"
                 style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22 numOctaves=%222%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")' }}
            />
        </div>
    );
}

const SLIDE_LABEL = {
    kpis: 'KPIs',
    milestones: 'Jalons',
    risks: 'Risques',
    decisions: 'Décisions',
};

function SlideKpis({ kpis, currency, fmt }) {
    const consumed = kpis.consumed_pct || 0;
    const consumedColor = consumed >= 90 ? 'text-red-400' : consumed >= 70 ? 'text-amber-300' : 'text-emerald-300';
    const animBudget = useCountUp(kpis.budget_planned);
    const animSpent = useCountUp(kpis.budget_spent);
    const animConsumed = useCountUp(consumed);
    const animTasks = useCountUp(kpis.tasks_count);
    const animPending = useCountUp(kpis.pending_approvals);
    const animProgress = useCountUp(kpis.progress);
    return (
        <div className="w-full max-w-6xl mx-auto">
            <div className="eyebrow text-primary mb-4 flex items-center gap-2 kiosk-card" style={{ animationDelay: '0ms' }}>
                <TrendingUp size={14} /> Tableau de bord exécutif
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                <KpiCard delay={80}  label="Budget planifié" value={fmt(Math.round(animBudget))} unit={currency} />
                <KpiCard delay={160} label="Dépensé" value={fmt(Math.round(animSpent))} unit={currency} />
                <KpiCard delay={240} label="Consommation" value={`${Math.round(animConsumed)} %`} accent={consumedColor} progress={consumed} />
                <KpiCard delay={320} label="Tâches" value={Math.round(animTasks)} sub={`${kpis.milestones_total} jalons`} />
            </div>
            <div className="grid grid-cols-2 gap-6 mt-6">
                <KpiCard delay={400} label="Validations en attente" value={Math.round(animPending)} accent={kpis.pending_approvals > 0 ? 'text-amber-300' : 'text-white'} />
                <KpiCard delay={480} label="Avancement projet" value={`${Math.round(animProgress)} %`} accent="text-primary" progress={kpis.progress} progressColor="bg-primary" />
            </div>
        </div>
    );
}

function KpiCard({ label, value, unit, sub, accent = 'text-white', progress, progressColor = 'bg-white/60', delay = 0 }) {
    return (
        <div
            className="kiosk-card group relative p-7 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm overflow-hidden transition-all duration-500 hover:bg-white/[0.07] hover:border-white/20 hover:-translate-y-1"
            style={{ animationDelay: `${delay}ms` }}
        >
            {/* Subtle inner glow */}
            <div className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                 style={{ background: 'radial-gradient(120% 80% at 0% 0%, rgba(212,114,47,0.18), transparent 60%)' }} />
            <div className="relative">
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 mb-3">{label}</div>
                <div className={`font-heading text-5xl leading-none tabular-nums ${accent}`}>{value}</div>
                {unit && <div className="text-xs text-white/40 mt-2">{unit}</div>}
                {sub && <div className="text-xs text-white/40 mt-2">{sub}</div>}
                {typeof progress === 'number' && (
                    <div className="mt-4 h-1 rounded-full bg-white/10 overflow-hidden">
                        <div
                            className={`h-full ${progressColor} transition-[width] duration-[1200ms] ease-out`}
                            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

function SlideMilestones({ items }) {
    return (
        <div className="w-full max-w-5xl mx-auto">
            <div className="eyebrow text-primary mb-4 flex items-center gap-2 kiosk-card">
                <Calendar size={14} /> Jalons à venir
            </div>
            {items.length === 0 ? (
                <div className="text-white/40 text-xl mt-12 italic kiosk-card" style={{ animationDelay: '120ms' }}>Aucun jalon imminent.</div>
            ) : (
                <ul className="space-y-3 mt-4">
                    {items.map((m, idx) => {
                        const days = daysFromNow(m.end_date);
                        const tone = days != null && days < 7 ? 'text-amber-300' : 'text-white';
                        return (
                            <li
                                key={m.id}
                                className="kiosk-card flex items-center justify-between p-5 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:translate-x-1 transition-all duration-300"
                                style={{ animationDelay: `${120 + idx * 90}ms` }}
                            >
                                <div>
                                    <div className="font-heading text-2xl text-white">{m.title}</div>
                                    <div className="text-xs text-white/40 mt-1 uppercase tracking-wider">
                                        {m.phase || 'Phase —'}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-mono text-sm text-white/60">{m.end_date}</div>
                                    <div className={`font-heading text-3xl mt-1 ${tone}`}>
                                        {days != null ? (days >= 0 ? `J-${days}` : `J+${-days}`) : '—'}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function SlideRisks({ items }) {
    return (
        <div className="w-full max-w-5xl mx-auto">
            <div className="eyebrow text-red-300 mb-4 flex items-center gap-2 kiosk-card">
                <AlertTriangle size={14} /> Top 3 risques en cours
            </div>
            {items.length === 0 ? (
                <div className="text-white/40 text-xl mt-12 italic kiosk-card" style={{ animationDelay: '120ms' }}>Aucun risque ouvert 🎉</div>
            ) : (
                <ul className="space-y-3 mt-4">
                    {items.map((r, idx) => (
                        <li
                            key={r.id}
                            className="kiosk-card p-6 rounded-xl border-l-4 border-red-400 bg-white/[0.04] hover:bg-white/[0.07] transition-colors duration-300"
                            style={{ animationDelay: `${120 + idx * 110}ms` }}
                        >
                            <div className="text-white text-xl leading-snug">{r.body}</div>
                            <div className="text-[11px] uppercase tracking-wider text-white/40 mt-3">
                                Soulevé · {new Date(r.created_at).toLocaleDateString('fr-FR')}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function SlideDecisions({ items }) {
    return (
        <div className="w-full max-w-5xl mx-auto">
            <div className="eyebrow text-emerald-300 mb-4 flex items-center gap-2 kiosk-card">
                <CheckCircle2 size={14} /> Dernières décisions
            </div>
            {items.length === 0 ? (
                <div className="text-white/40 text-xl mt-12 italic kiosk-card" style={{ animationDelay: '120ms' }}>Aucune décision enregistrée.</div>
            ) : (
                <ul className="space-y-3 mt-4">
                    {items.map((d, idx) => (
                        <li
                            key={d.id}
                            className="kiosk-card p-6 rounded-xl border-l-4 border-emerald-300 bg-white/[0.04] hover:bg-white/[0.07] transition-colors duration-300"
                            style={{ animationDelay: `${120 + idx * 90}ms` }}
                        >
                            <div className="text-white text-xl leading-snug">{d.body}</div>
                            <div className="text-[11px] uppercase tracking-wider text-white/40 mt-3 flex items-center gap-2">
                                {d.is_resolved && <span className="text-emerald-300">✓ Appliquée</span>}
                                · {new Date(d.created_at).toLocaleDateString('fr-FR')}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
